import { Box, Button, Link, Skeleton } from "@mui/material";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { fillCopy } from "helpers/methods";
import { getSeatUsage } from "helpers/perSeatHelpers";
import myPlanTokens, { myPlanResponsiveTokens } from "../../../MyPlan.tokens";
import {
  ghostButtonSx,
  pillSx,
  planCardSx,
  planNameSx,
  sectionDescSx,
  statLabelSx,
  statValueSx,
} from "../../../MyPlan.styles";

const t = myPlanTokens;
const r = myPlanResponsiveTokens;
const copy = strings.awsMarketplaceBilling;

interface Props {
  isLoading: boolean;
  currentPlan: any;
  accountStats: any;
}

/**
 * The plan card for an account billed by AWS Marketplace. It replaces
 * PlanDetails' change-plan / cancel / manage-seats actions: AWS owns all of
 * them, and core refuses a Stripe purchase on this account with a 409.
 */
const AwsMarketplaceBilling = ({
  isLoading,
  currentPlan,
  accountStats,
}: Props) => {
  const seats = getSeatUsage(accountStats);

  return (
    <Box sx={planCardSx}>
      <Box
        sx={{
          px: { xs: r.gutter, md: t.space.gutter },
          py: { xs: r.headerY, md: t.space.planHeaderY },
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          borderBottom: `${t.border.hairline} solid ${t.color.rowDivider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {isLoading ? (
            <Skeleton width={160} height={28} />
          ) : (
            <Box component="h2" sx={planNameSx}>
              {currentPlan?.name ?? "No Plan"}
            </Box>
          )}
          <Box
            component="span"
            sx={pillSx({
              color: t.color.cycleBadgeText,
              bgcolor: t.color.cycleBadgeBg,
              fontSize: t.font.cycleBadge,
              letterSpacing: t.letterSpacing.cycleBadge,
              paddingY: "3px",
              uppercase: true,
            })}
          >
            {copy.badge}
          </Box>
        </Box>
        <Box component="span" sx={sectionDescSx}>
          {copy.description}
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: r.gutter, md: t.space.gutter },
          py: { xs: r.headerY, md: t.space.planHeaderY },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: r.headerGap, md: "24px" },
        }}
      >
        {seats.limit > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Box component="span" sx={statLabelSx}>
              {copy.seatsLabel}
            </Box>
            {isLoading ? (
              <Skeleton width={120} height={28} />
            ) : (
              <Box component="span" sx={statValueSx(t.color.ink)}>
                {fillCopy(copy.seatsValue, {
                  used: seats.used,
                  limit: seats.limit,
                })}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Box component="span" sx={sectionDescSx}>
            {copy.manageNote} {copy.cancelNotePrefix}
            <Link
              href={`mailto:${strings.inkfreeSupportEmail}`}
              sx={{ color: t.color.accent, fontWeight: t.weight.strong }}
            >
              {strings.inkfreeSupportEmail}
            </Link>
            .
          </Box>
        </Box>
        <Button
          disableElevation
          sx={ghostButtonSx}
          href={urls.AWS_MARKETPLACE_SUBSCRIPTIONS}
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy.manageButton}
        </Button>
      </Box>
    </Box>
  );
};

export default AwsMarketplaceBilling;
